/* eslint-disable react/prop-types */
import { useState, useMemo, useEffect } from "react";
import { createPortal } from "react-dom";
import { useNavigate } from "react-router-dom";
import PropTypes from "prop-types";
import { motion, AnimatePresence } from "framer-motion";
import { FiEdit2, FiTrash2, FiX } from "react-icons/fi";
import { toast } from "react-toastify";
import {
  PREDEFINED_MODES_MAP,
  getRegisteredCameras,
  editCamera,
  deleteCamera,
  startProcessing,
  startRtspAudioProcessing,
  startRtspBounceAudioProcessing,
} from "../Api";

/* ────────────────────────── showcase prompts ───────────────────────── */
const SHOWCASE_PROMPTS = {
  "Traffic Analyzer":
    "Detect and count all incoming/outgoing vehicles and extract license plate numbers using OCR.Use Cases: Parking lot monitoring, traffic flow, gated community logs Industries: Retail, Warehousing, Real Estate, Hospitality.",
  "Footfall Tracker":
    "Count total footfall in a given area using camera feeds — in real-time or over time. Use Cases: Store analytics, occupancy tracking, event crowd measurement.Industries: Retail, Healthcare, Malls, Education, Transportation.",
  "Queue Monitor":
    "Monitor the size and movement of queues to detect long wait times or congestion.Use Cases: Billing counters, ticket lines, pharmacy windows.Industries: Retail, Clinics, Airports, Banks.",
  "Customer Service Monitor":
    "Analyze audio from customer interactions (Live Mic, calls, meetings, voicemails, or audio extracted from video files/streams) to determine sentiment, key concerns, and solutions provided. Use Cases: Enhance customer satisfaction, identify service improvement areas, staff training. Industries: Call Centers, Retail, Healthcare, Support Desks.",
  "Idle Employee Tracker":
    "Monitor a designated employee area (e.g., workstation, counter) and log periods when no person is detected within the defined zone. Use Cases: Track staff presence, identify unattended posts, analyze workflow gaps. Industries: Retail, Clinics, Reception, Security.",
  "Waiting Time Tracker":
    "Monitor customer waiting times in queues and service areas to optimize efficiency and reduce wait times. Tracks zone entry/exit events and measures actual waiting durations. Use Cases: Service counters, checkout lines, appointment waiting areas, customer service desks. Industries: Retail, Healthcare, Government Services, Banking, Hospitality.",
  "Bounce Detector":
    "Identify customers who leave shortly after an interaction without buying anything. Source can be a Live Mic, RTSP audio stream, or an uploaded file/link. Use Cases: Conversion tracking, staff performance, sales optimisation. Industries: Retail, Pharmacies, Clinics.",
  "AI Detection Agent":
    "This agent uses advanced AI models for general-purpose detection tasks based on a custom textual prompt you provide or a default one. Describe what you want to detect or analyse in the video stream. Use Cases: Custom object detection, scene analysis, activity recognition.",
};

/* ───────────────────── framer-motion helpers ──────────────────────── */
const variants = {
  enter: (d) => ({
    x: d > 0 ? "100%" : "-100%",
    opacity: 0,
    position: "absolute",
    width: "100%",
  }),
  center: { zIndex: 1, x: 0, opacity: 1, position: "relative", width: "100%" },
  exit: (d) => ({
    zIndex: 0,
    x: d < 0 ? "100%" : "-100%",
    opacity: 0,
    position: "absolute",
    width: "100%",
  }),
};
const transition = {
  x: { type: "spring", stiffness: 350, damping: 35 },
  opacity: { duration: 0.2 },
};

/* classify a registration by its URL */
const sourceKindFromUrl = (url = "") => {
  const u = url.toLowerCase();
  if (u.startsWith("live_mic_source_")) return "live_mic";
  if (u.startsWith("rtsp://")) return "rtsp";
  return "local";
};

/* get formatted camera name with analysis mode for display */
const getCameraDisplayName = (camera) => {
  if (camera.agent === "Customer Service Monitor" && camera.analysis_mode) {
    const modeLabels = {
      "Customer_sentiment_analyzer": "Customer Sentiment Analyzer",
      "Referral_tracker": "Referral Tracker",
      "Diversion_tracker": "Diversion Tracker"
    };
    const modeLabel = modeLabels[camera.analysis_mode] || camera.analysis_mode;
    return `${camera.name} - ${modeLabel}`;
  }
  return camera.name;
};

/* get unique camera identifier including analysis mode */
const getCameraUniqueId = (camera) => {
  if (camera.agent === "Customer Service Monitor" && camera.analysis_mode) {
    const baseId = camera.id || camera.url;
    return `${baseId}_${camera.analysis_mode}`;
  }
  return camera.id || camera.url;
};

/* ────────────────────────── Enhanced Modal Portal Component ───────────────────────── */
const ModalPortal = ({ children, isOpen, onClose }) => {
  const previousActiveElement = useMemo(() => document.activeElement, []);
  
  useEffect(() => {
    if (isOpen) {
      // Lock body scroll when modal opens
      document.body.classList.add('modal-open');
      
      // Handle ESC key press
      const handleEscape = (e) => {
        if (e.key === 'Escape' && onClose) {
          onClose();
        }
      };
      
      // Handle tab key for focus trapping
      const handleTab = (e) => {
        if (e.key === 'Tab') {
          const modalRoot = document.getElementById('modal-root');
          if (modalRoot) {
            const focusableElements = modalRoot.querySelectorAll(
              'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
            );
            const firstElement = focusableElements[0];
            const lastElement = focusableElements[focusableElements.length - 1];
            
            if (e.shiftKey) {
              if (document.activeElement === firstElement) {
                lastElement?.focus();
                e.preventDefault();
              }
            } else {
              if (document.activeElement === lastElement) {
                firstElement?.focus();
                e.preventDefault();
              }
            }
          }
        }
      };
      
      document.addEventListener('keydown', handleEscape);
      document.addEventListener('keydown', handleTab);
      
      // Focus first focusable element in modal
      setTimeout(() => {
        const modalRoot = document.getElementById('modal-root');
        const firstFocusable = modalRoot?.querySelector(
          'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
        );
        firstFocusable?.focus();
      }, 100);
      
      // Cleanup function
      return () => {
        document.body.classList.remove('modal-open');
        document.removeEventListener('keydown', handleEscape);
        document.removeEventListener('keydown', handleTab);
        // Restore focus to previous element
        if (previousActiveElement && typeof previousActiveElement.focus === 'function') {
          previousActiveElement.focus();
        }
      };
    } else {
      // Ensure body scroll is unlocked when modal closes
      document.body.classList.remove('modal-open');
    }
  }, [isOpen, onClose, previousActiveElement]);
  
  if (!isOpen) return null;
  
  // Create modal root if it doesn't exist
  let modalRoot = document.getElementById('modal-root');
  if (!modalRoot) {
    modalRoot = document.createElement('div');
    modalRoot.id = 'modal-root';
    modalRoot.setAttribute('role', 'dialog');
    modalRoot.setAttribute('aria-modal', 'true');
    document.body.appendChild(modalRoot);
  }
  
  return createPortal(children, modalRoot);
};

/* ─────────────────────────── component ───────────────────────────── */
export default function CameraForm({ onSubmit, isLoading }) {
  const navigate = useNavigate();
  
  /* ───── basic state ───── */
  const [step, setStep] = useState(1);
  const [direction, setDirection] = useState(1);
  const [selectedAnalysisOption, setSelectedAnalysisOption] = useState("");
  const [srcType, setSrcType] = useState("rtsp");
  const [url, setUrl] = useState("");
  const [file, setFile] = useState(null);
  const [error, setError] = useState(null);
  const [cameraName, setCameraName] = useState("");
  const [cameraLocation, setCameraLocation] = useState("");
  const [selectedAnalysisMode, setSelectedAnalysisMode] = useState("Customer_sentiment_analyzer"); // NEW
  const [showAgentAnimation, setShowAgentAnimation] = useState(false);
  const [micVideoRtspUrl, setMicVideoRtspUrl] = useState(""); // ← NEW

  // Multi-camera selection state
  const [selectedCameraIds, setSelectedCameraIds] = useState([]);

  const [registeredCamerasForCurrentAgent, setRegisteredCamerasForCurrentAgent] =
    useState([]);
  const [isFetchingCameras, setIsFetchingCameras] = useState(false);

  // Edit/Delete state
  const [editingCamera, setEditingCamera] = useState(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(null);
  const [editFormData, setEditFormData] = useState({
    name: "",
    location: "",
    url: "",
    agent: "",
    analysis_mode: "Customer_sentiment_analyzer" // NEW
  });

  const analysisOptions = useMemo(
    () => [...Object.keys(PREDEFINED_MODES_MAP)],
    []
  );

  const isComingSoonSelected = useMemo(
    () => selectedAnalysisOption.toLowerCase().includes("(coming soon)"),
    [selectedAnalysisOption]
  );

  /* ───── helper booleans ───── */
  const isCustomerServiceMonitorSelected =
    selectedAnalysisOption === "Customer Service Monitor";
  const isBounceDetectorSelected = selectedAnalysisOption === "Bounce Detector";

  const isAnyAudioModeSelected = useMemo(() => {
    const backendMode = PREDEFINED_MODES_MAP[selectedAnalysisOption];
    return (
      backendMode === "Audio Sentiment Analysis" ||
      backendMode === "Bounce Audio Analysis"
    );
  }, [selectedAnalysisOption]);

  const isLiveMicSupported =
    isCustomerServiceMonitorSelected || isBounceDetectorSelected;

  /* ───── reset on agent change ───── */
  useEffect(() => {
    setUrl("");
    setFile(null);
    setError(null);
    setCameraName("");
    setCameraLocation("");
    setSelectedAnalysisMode("Customer_sentiment_analyzer"); // Reset analysis mode when agent changes
    setMicVideoRtspUrl(""); // ← NEW
    setSelectedCameraIds([]); // Reset selected cameras when agent changes

    if (isCustomerServiceMonitorSelected || isBounceDetectorSelected) {
      setSrcType("rtsp");
    } else if (isAnyAudioModeSelected) {
      setSrcType("local");
    } else {
      setSrcType("rtsp");
    }
  }, [
    selectedAnalysisOption,
    isAnyAudioModeSelected,
    isCustomerServiceMonitorSelected,
    isBounceDetectorSelected,
  ]);

  /* ───── fetch registered cams ───── */
  useEffect(() => {
    const shouldFetch =
      step === 2 && selectedAnalysisOption && !isComingSoonSelected;

    if (!shouldFetch) {
      setRegisteredCamerasForCurrentAgent([]);
      return;
    }

    const fetchCameras = async () => {
      setIsFetchingCameras(true);
      setError(null);
      try {
        const allCameras = await getRegisteredCameras();
        const filtered = allCameras.filter(
          (cam) => cam.agent === selectedAnalysisOption
        );
        setRegisteredCamerasForCurrentAgent(filtered);
      } catch (err) {
        console.error("Failed to fetch registered cameras:", err);
        setRegisteredCamerasForCurrentAgent([]);
      } finally {
        setIsFetchingCameras(false);
      }
    };
    fetchCameras();
  }, [step, selectedAnalysisOption, isComingSoonSelected]);

  /* ───── prompt text ───── */
  const displayedShowcasePrompt = useMemo(() => {
    if (isComingSoonSelected)
      return `The "${selectedAnalysisOption.replace(
        " (coming soon)",
        ""
      )}" feature is currently under development...`;
    return (
      SHOWCASE_PROMPTS[selectedAnalysisOption] ||
      "Select an agent to see details."
    );
  }, [selectedAnalysisOption, isComingSoonSelected]);

  /* ───── helpers ───── */
  const getBackendParams = () => {
    const backend_mode_string =
      PREDEFINED_MODES_MAP[selectedAnalysisOption] || "AI Detection";
    let custom_prompt_text = null;
    if (
      selectedAnalysisOption === "AI Detection Agent" &&
      backend_mode_string === "AI Detection"
    ) {
      custom_prompt_text =
        SHOWCASE_PROMPTS["AI Detection Agent"] ||
        "Perform general AI analysis.";
    }
    return { backend_mode_string, custom_prompt_text };
  };

  /* ───── navigation handlers ───── */
  const handleNextStep = () => {
    setError(null);
    if (step === 1) {
      if (!selectedAnalysisOption) {
        setError("Please select an agent.");
        return;
      }
      if (isComingSoonSelected) {
        setError("This agent is coming soon and cannot be selected.");
        return;
      }
      setDirection(1);
      setStep(2);
    }
  };
  const handleBackStep = () => {
    if (step === 2) {
      setDirection(-1);
      setStep(1);
      setError(null);
    }
  };

  /* ───── filter registered list by srcType ───── */
  const filteredRegisteredCameras = useMemo(
    () =>
      registeredCamerasForCurrentAgent.filter(
        (cam) => sourceKindFromUrl(cam.url) === srcType
      ),
    [registeredCamerasForCurrentAgent, srcType]
  );

  /* ───── click registered source card ───── */
  const handleCameraCardClick = (camera) => {
    // Always toggle selection when clicking a card
    handleCameraCheckboxChange(camera);

    // If this is the first/only selected camera, also populate the form fields
    const cameraId = getCameraUniqueId(camera);
    const isCurrentlySelected = selectedCameraIds.includes(cameraId);

    if (!isCurrentlySelected && selectedCameraIds.length === 0) {
      // This will be the first selected camera, populate form fields
      setCameraName(camera.name);
      setCameraLocation(camera.location || "");

      // Set analysis mode for Customer Service Monitor cameras
      if (isCustomerServiceMonitorSelected && camera.analysis_mode) {
        setSelectedAnalysisMode(camera.analysis_mode);
      }

      const lowerUrl = camera.url.toLowerCase();
      if (lowerUrl.startsWith("live_mic_source_")) {
        setSrcType("live_mic");
        setUrl("");
        setFile(null);
      } else if (lowerUrl.startsWith("rtsp://")) {
        setSrcType("rtsp");
        setUrl(camera.url);
        setFile(null);
      } else {
        setSrcType("local");
        setUrl(camera.url);
        setFile(null);
      }
    }

    setError(null);
  };

  /* ───── checkbox selection handlers ───── */
  const handleCameraCheckboxChange = (camera) => {
    const cameraId = getCameraUniqueId(camera);
    setSelectedCameraIds(prev => {
      if (prev.includes(cameraId)) {
        return prev.filter(id => id !== cameraId);
      } else {
        return [...prev, cameraId];
      }
    });
    setError(null);
  };

  const handleSelectAll = () => {
    const allCameraIds = filteredRegisteredCameras.map(cam => getCameraUniqueId(cam));
    setSelectedCameraIds(allCameraIds);
    setError(null);
  };

  const handleDeselectAll = () => {
    setSelectedCameraIds([]);
    setError(null);
  };

  /* ───── edit/delete handlers ───── */
  const handleEditClick = (e, camera) => {
    e.preventDefault(); // Prevent any form submission
    e.stopPropagation(); // Prevent camera card click
    setError(null); // Clear any existing errors
    setEditFormData({
      name: camera.name,
      location: camera.location || "",
      url: camera.url,
      agent: camera.agent,
      analysis_mode: camera.analysis_mode || "Customer_sentiment_analyzer"
    });
    setEditingCamera(camera);
  };

  const handleDeleteClick = (e, camera) => {
    e.preventDefault(); // Prevent any form submission
    e.stopPropagation(); // Prevent camera card click
    setError(null); // Clear any existing errors
    setShowDeleteConfirm(camera);
  };

  const handleEditSubmit = async (e) => {
    e.preventDefault();
    if (!editingCamera) return;

    try {
      await editCamera(editingCamera.id, editFormData);
      toast.success(`Camera "${editFormData.name}" updated successfully!`);
      
      // Refresh the camera list
      const allCameras = await getRegisteredCameras();
      const filtered = allCameras.filter(
        (cam) => cam.agent === selectedAnalysisOption
      );
      setRegisteredCamerasForCurrentAgent(filtered);
      
      // Close edit modal
      setEditingCamera(null);
      setEditFormData({ name: "", location: "", url: "", agent: "" });
    } catch (error) {
      console.error("Failed to edit camera:", error);
      toast.error(`Failed to update camera: ${error.message}`);
    }
  };

  const handleDeleteConfirm = async () => {
    if (!showDeleteConfirm) return;

    try {
      await deleteCamera(showDeleteConfirm.id);
      toast.success(`Camera "${showDeleteConfirm.name}" deleted successfully!`);
      
      // Refresh the camera list
      const allCameras = await getRegisteredCameras();
      const filtered = allCameras.filter(
        (cam) => cam.agent === selectedAnalysisOption
      );
      setRegisteredCamerasForCurrentAgent(filtered);
      
      // Close delete modal
      setShowDeleteConfirm(null);
    } catch (error) {
      console.error("Failed to delete camera:", error);
      toast.error(`Failed to delete camera: ${error.message}`);
    }
  };

  const handleEditCancel = () => {
    setEditingCamera(null);
    setEditFormData({ name: "", location: "", url: "", agent: "", analysis_mode: "Customer_sentiment_analyzer" });
  };

  const handleDeleteCancel = () => {
    setShowDeleteConfirm(null);
  };

  /* ───── form submit ───── */
  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);

    if (isComingSoonSelected) {
      setError(`Agent "${selectedAnalysisOption}" is coming soon.`);
      return;
    }
    if (!selectedAnalysisOption) {
      setError("Please select an agent (Step 1).");
      setStep(1);
      return;
    }

    // If multiple cameras are selected, use bulk processing
    if (selectedCameraIds.length > 1) {
      return await handleBulkProcessing();
    }

    const { backend_mode_string, custom_prompt_text } = getBackendParams();
    const trimmedCameraName = cameraName.trim();
    const trimmedUrl = url.trim();
    const trimmedMicVideoUrl = micVideoRtspUrl.trim();

    /* basic validation */
    if (isLiveMicSupported && srcType === "live_mic") {
      if (!trimmedCameraName) {
        setError("Please provide a name for the Live Mic source.");
        return;
      }
      if (
        trimmedMicVideoUrl &&
        !trimmedMicVideoUrl.toLowerCase().startsWith("rtsp://")
      ) {
        setError("Companion video URL must start with rtsp://");
        return;
      }
    } else {
      if (!isAnyAudioModeSelected && !trimmedCameraName) {
        setError("Please provide a name for the camera/video source.");
        return;
      }
      if (srcType === "rtsp" && !trimmedUrl) {
        setError(`Please provide an RTSP URL for ${selectedAnalysisOption}.`);
        return;
      }
      if (srcType === "local" && !file && !trimmedUrl) {
        setError(
          `For ${selectedAnalysisOption}, choose a local file or paste an S3/HTTP(S) link.`
        );
        return;
      }
      if (srcType === "local" && file) {
        const isAudio = file.type.startsWith("audio/");
        const isVideo = file.type.startsWith("video/");
        if (isAnyAudioModeSelected) {
          if (!isAudio && !isVideo) {
            setError(
              `For ${selectedAnalysisOption}, upload an audio or video file.`
            );
            return;
          }
        } else if (!isVideo) {
          setError(`For ${selectedAnalysisOption}, upload a video file.`);
          return;
        }
      }
    }

    /* dedup check & uploads unchanged … (omitted for brevity) */

    // Handle single camera or use form fields if no cameras selected
    if (selectedCameraIds.length === 1) {
      // Use the selected camera for processing
      const selectedCamera = filteredRegisteredCameras.find(cam =>
        selectedCameraIds.includes(getCameraUniqueId(cam))
      );

      if (selectedCamera) {
        const submitPayload = {
          analysis_category: selectedAnalysisOption,
          selected_mode: backend_mode_string,
          camera_name: selectedCamera.name,
          camera_location: selectedCamera.location || "",
          source_type: sourceKindFromUrl(selectedCamera.url) === "rtsp" ? "rtsp" : sourceKindFromUrl(selectedCamera.url) === "live_mic" ? "live_mic" : "local",
          source_path: selectedCamera.url,
          uploaded_path: null,
          file_obj_from_form: null,
          custom_prompt_text,
          is_existing_camera_from_form: true, // This is an existing registered camera
          mic_video_rtsp_url: "", // Not applicable for existing cameras
        };

        // Add analysis_mode for Customer Service Monitor
        if (isCustomerServiceMonitorSelected && selectedCamera.analysis_mode) {
          submitPayload.analysis_mode = selectedCamera.analysis_mode;
        }

        await onSubmit(submitPayload);
        return;
      }
    }

    /* build payload for new camera */
    try {
      const submitPayload = {
        analysis_category: selectedAnalysisOption,
        selected_mode: backend_mode_string,
        camera_name: trimmedCameraName,
        camera_location: cameraLocation.trim(),
        source_type: srcType,
        source_path:
          srcType === "live_mic"
            ? `mic_placeholder_${trimmedCameraName
                .replace(/\s+/g, "_")
                .toLowerCase()}`
            : trimmedUrl,
        /* upload / file handling left unchanged */
        uploaded_path: null,
        file_obj_from_form: null,
        custom_prompt_text,
        is_existing_camera_from_form: false,
        mic_video_rtsp_url: trimmedMicVideoUrl,// ← NEW
      };

      // Add analysis_mode for Customer Service Monitor
      if (isCustomerServiceMonitorSelected) {
        submitPayload.analysis_mode = selectedAnalysisMode;
      }

      await onSubmit(submitPayload);
    } catch (err) {
      console.error("[CameraForm] submission error:", err);
      setError(err.message || "An unexpected error occurred.");
    }
  };

  /* ───── bulk processing handler ───── */
  const handleBulkProcessing = async () => {
    if (selectedCameraIds.length === 0) {
      setError("Please select at least one camera for bulk processing.");
      return;
    }

    if (!selectedAnalysisOption) {
      setError("Please select an agent first.");
      return;
    }

    // Get selected cameras for validation
    const selectedCameras = registeredCamerasForCurrentAgent.filter(cam =>
      selectedCameraIds.includes(getCameraUniqueId(cam))
    );

    // Pre-processing validation: Check for duplicate camera-mode combinations
    if (selectedAnalysisOption === "Customer Service Monitor") {
      const combinationCounts = new Map();
      selectedCameras.forEach(camera => {
        const cameraAnalysisMode = camera.analysis_mode || selectedAnalysisMode;
        const combinationKey = `${camera.name}_${cameraAnalysisMode}`;
        combinationCounts.set(combinationKey, (combinationCounts.get(combinationKey) || 0) + 1);
      });

      const duplicates = Array.from(combinationCounts.entries()).filter(([key, count]) => count > 1);
      if (duplicates.length > 0) {
        const duplicateInfo = duplicates.map(([key, count]) => `${key} (${count} times)`).join(', ');
        console.warn(`[Bulk Processing Validation] Duplicate combinations detected:`, duplicateInfo);
        toast.warn(`Duplicate camera-mode combinations detected: ${duplicateInfo}. These will be deduplicated during processing.`);
      }
    }

    // For audio modes, validate that all selected sources are RTSP
    if (isAnyAudioModeSelected) {
      const nonRtspSources = selectedCameras.filter(cam => 
        sourceKindFromUrl(cam.url) !== "rtsp"
      );
      
      if (nonRtspSources.length > 0) {
        setError(`Bulk processing for ${selectedAnalysisOption} only supports RTSP sources. Please deselect non-RTSP sources: ${nonRtspSources.map(cam => cam.name).join(", ")}`);
        return;
      }
    }

    const { backend_mode_string, custom_prompt_text } = getBackendParams();

    try {
      // Handle audio modes with individual API calls
      if (isAnyAudioModeSelected) {
        return await handleAudioBulkProcessing(selectedCameras, selectedAnalysisOption);
      }

      const cameras_list = selectedCameras.map(cam => ({
        camera_id: cam.name,
        source_type: sourceKindFromUrl(cam.url) === "rtsp" ? "rtsp" : "video_file",
        source_path: cam.url,
      }));

      const startPayload = {
        cameras_list,
        selected_mode: backend_mode_string,
      };

      if (selectedAnalysisOption === "AI Detection Agent" && custom_prompt_text) {
        startPayload.custom_prompt_text = custom_prompt_text;
      }

      console.log("[Bulk Processing] Starting with payload:", startPayload);
      
      // Call the startProcessing API directly for bulk processing
      const multiResp = await startProcessing(startPayload);
      
      if (multiResp.previous_process_stopped) {
        toast.info("A previous analysis process was stopped.");
      }
      
      if (!multiResp.instances?.length) {
        throw new Error(multiResp.overall_message || "Failed to start bulk processing.");
      }

      // Show success messages for each instance
      let successCount = 0;
      let failureCount = 0;
      
      multiResp.instances.forEach(inst => {
        if (["started", "already_running_this_source_mode", "already_running"].includes(inst.status)) {
          successCount++;
          toast.success(`${inst.status === "started" ? "Started" : "Found existing"} ${selectedAnalysisOption} for ${inst.camera_id}.`);
        } else {
          failureCount++;
          toast.error(`Failed to start ${selectedAnalysisOption} for ${inst.camera_id}: ${inst.message}`);
        }
      });

      if (successCount > 0) {
        toast.success(`Bulk processing completed: ${successCount} cameras started successfully.`);
      }
      
      if (failureCount > 0) {
        toast.warn(`${failureCount} cameras failed to start. Check individual messages above.`);
      }

      // Clear selections after successful submission
      setSelectedCameraIds([]);
      
      // Navigate to view page
      navigate("/view", {
        state: {
          activeMode: selectedAnalysisOption,
          bulkProcessingResult: {
            totalCameras: cameras_list.length,
            successCount,
            failureCount,
            instances: multiResp.instances
          }
        }
      });
      
    } catch (err) {
      console.error("[CameraForm] bulk processing error:", err);
      setError(err.message || "An error occurred during bulk processing.");
    }
  };

  /* ───── audio bulk processing handler ───── */
  const handleAudioBulkProcessing = async (selectedCameras, analysisOption) => {
    let successCount = 0;
    let failureCount = 0;
    const results = [];

    // Debug logging to identify duplicate processing issue
    console.log(`[Bulk Processing Debug] Total registered cameras for ${analysisOption}:`, registeredCamerasForCurrentAgent.length);
    console.log(`[Bulk Processing Debug] Selected camera IDs:`, selectedCameraIds);
    console.log(`[Bulk Processing Debug] Filtered selected cameras:`, selectedCameras.length);

    // Log each camera that will be processed
    selectedCameras.forEach((camera, index) => {
      console.log(`[Bulk Processing Debug] Camera ${index + 1}:`, {
        name: camera.name,
        id: camera.id,
        url: camera.url,
        analysis_mode: camera.analysis_mode,
        uniqueId: getCameraUniqueId(camera)
      });
    });

    // Deduplication: Remove cameras with same name+analysis_mode combination
    const processedCombinations = new Set();
    const uniqueCameras = selectedCameras.filter(camera => {
      const cameraAnalysisMode = camera.analysis_mode || selectedAnalysisMode;
      const combinationKey = `${camera.name}_${cameraAnalysisMode}`;

      if (processedCombinations.has(combinationKey)) {
        console.log(`[Bulk Processing Debug] Skipping duplicate:`, {
          name: camera.name,
          analysis_mode: cameraAnalysisMode,
          combinationKey
        });
        return false;
      }

      processedCombinations.add(combinationKey);
      return true;
    });

    if (uniqueCameras.length !== selectedCameras.length) {
      const duplicateCount = selectedCameras.length - uniqueCameras.length;
      console.log(`[Bulk Processing Debug] Removed ${duplicateCount} duplicate camera-mode combinations`);
      toast.warn(`Removed ${duplicateCount} duplicate camera-mode combinations. Processing ${uniqueCameras.length} unique combinations.`);
    }

    toast.info(`Starting bulk processing for ${uniqueCameras.length} ${analysisOption} sources...`);

    // Process cameras sequentially to avoid overwhelming the server
    for (const camera of uniqueCameras) {
      try {
        const payload = { 
          rtsp_url: camera.url,
          camera_location: camera.location || ""
        };

        // Generate mode-specific readable stream IDs
        if (analysisOption === "Customer Service Monitor") {
          // Use individual camera's analysis mode, fallback to form's selected mode
          const cameraAnalysisMode = camera.analysis_mode || selectedAnalysisMode;

          if (!cameraAnalysisMode) {
            throw new Error(`Camera "${camera.name}" is missing analysis mode configuration`);
          }

          payload.analysis_mode = cameraAnalysisMode;
          const baseId = camera.name;
          const modeId = cameraAnalysisMode.toLowerCase().replace(/_/g, '-');
          payload.stream_id = `${baseId}_${modeId}`;

          console.log(`[Bulk Processing] Starting ${cameraAnalysisMode} for camera: ${camera.name}`);
        } else {
          // For other modes (like Bounce Detector), use simple camera name
          payload.stream_id = camera.name;
        }

        // Enhanced progress display
        const currentIndex = uniqueCameras.indexOf(camera) + 1;
        const progressMessage = analysisOption === "Customer Service Monitor"
          ? `Processing camera ${currentIndex} of ${uniqueCameras.length}: ${camera.name} (${camera.analysis_mode || selectedAnalysisMode})`
          : `Processing camera ${currentIndex} of ${uniqueCameras.length}: ${camera.name}`;

        console.log(`[Bulk Processing Progress] ${progressMessage}`);
        toast.info(progressMessage, { autoClose: 2000 });

        let audioResp;
        if (analysisOption === "Customer Service Monitor") {
          audioResp = await startRtspAudioProcessing(payload);
        } else if (analysisOption === "Bounce Detector") {
          audioResp = await startRtspBounceAudioProcessing(payload);
        } else {
          throw new Error(`Unsupported audio analysis option: ${analysisOption}`);
        }

        if (audioResp?.stream_id) {
          successCount++;
          // Show which specific analysis mode was started for Customer Service Monitor
          const displayMessage = analysisOption === "Customer Service Monitor"
            ? `Started ${analysisOption} (${camera.analysis_mode || selectedAnalysisMode}) for ${camera.name}`
            : `Started ${analysisOption} for ${camera.name}`;
          toast.success(displayMessage);
          results.push({
            camera: camera,
            status: "success",
            streamId: audioResp.stream_id,
            response: audioResp
          });
        } else {
          throw new Error(audioResp?.message || "No stream ID returned");
        }

      } catch (err) {
        failureCount++;
        // Show which specific analysis mode failed for Customer Service Monitor
        const errorMessage = analysisOption === "Customer Service Monitor"
          ? `Failed to start ${analysisOption} (${camera.analysis_mode || selectedAnalysisMode}) for ${camera.name}: ${err.message}`
          : `Failed to start ${analysisOption} for ${camera.name}: ${err.message}`;
        toast.error(errorMessage);
        results.push({
          camera: camera,
          status: "failed",
          error: err.message
        });
      }
    }

    // Show overall summary
    if (successCount > 0) {
      toast.success(`Audio bulk processing completed: ${successCount} sources started successfully.`);
    }
    
    if (failureCount > 0) {
      toast.warn(`${failureCount} sources failed to start. Check individual messages above.`);
    }

    // Clear selections and navigate
    setSelectedCameraIds([]);
    
    // Navigate to view page with first successful result if any
    const firstSuccess = results.find(r => r.status === "success");
    if (firstSuccess) {
      navigate("/view", {
        state: {
          activeMode: analysisOption,
          audioTaskType: "rtsp",
          audioStreamId: firstSuccess.streamId,
          audioOriginalSource: firstSuccess.camera.url,
          logFile: firstSuccess.response.log_file,
          bulkProcessingResult: {
            totalCameras: uniqueCameras.length,
            successCount,
            failureCount,
            results
          }
        }
      });
    } else {
      // All failed, stay on current page
      navigate("/view");
    }
  };

  /* ───── file picker helpers (unchanged) ───── */
  const handleFileSelection = (selectedFile) => {
    if (!selectedFile) {
      setFile(null);
      return;
    }
    const isAudio = selectedFile.type.startsWith("audio/");
    const isVideo = selectedFile.type.startsWith("video/");
    const valid =
      isAnyAudioModeSelected ? isAudio || isVideo : isVideo;
    setFile(valid ? selectedFile : null);
    if (!valid) {
      setError(
        isAnyAudioModeSelected
          ? "Upload an audio or video file."
          : "Upload a video file."
      );
    } else {
      setUrl("");
      setError(null);
    }
  };
  const dropFile = (e) => {
    e.preventDefault();
    handleFileSelection(e.dataTransfer.files?.[0] || null);
  };
  const pickFile = (e) => handleFileSelection(e.target.files?.[0] || null);

  const fileInputAcceptType = isAnyAudioModeSelected
    ? "audio/*,video/*"
    : "video/*";
  const fileInputPlaceholder = isAnyAudioModeSelected
    ? "Click or drop audio/video file"
    : "Click or drop video file";

  /* ───── step-2 UI ───── */
  const step2Content = (
    <motion.div
      key="step2"
      custom={direction}
      variants={variants}
      initial="enter"
      animate="center"
      exit="exit"
      transition={transition}
      className="form-step"
    >
      <label className="dropdown-label" style={{ fontSize: "1.1em" }}>
        Choose source for <strong>{selectedAnalysisOption}</strong>
      </label>

      {/* radio buttons */}
      <div className="radio-row" style={{ marginBottom: "1rem" }}>
        {isLiveMicSupported && (
          <label>
            <input
              type="radio"
              value="live_mic"
              checked={srcType === "live_mic"}
              onChange={() => {
                setSrcType("live_mic");
                setFile(null);
                setUrl("");
                setError(null);
              }}
            />
            Live Mic
          </label>
        )}
        <label>
          <input
            type="radio"
            value="rtsp"
            checked={srcType === "rtsp"}
            onChange={() => {
              setSrcType("rtsp");
              setFile(null);
              setUrl("");
              setError(null);
            }}
          />
          {isAnyAudioModeSelected ? "Stream (RTSP)" : "RTSP / Camera"}
        </label>
        <label>
          <input
            type="radio"
            value="local"
            checked={srcType === "local"}
            onChange={() => {
              setSrcType("local");
              setUrl("");
              setFile(null);
              setError(null);
            }}
          />
          {isAnyAudioModeSelected ? "File / Link" : "Local Video / Link"}
        </label>
      </div>

      <div className="step2-layout-wrapper">
        <div className="step2-main-input-area">
          {/* name */}
          <input
            className="camera-input"
            placeholder={
              srcType === "live_mic"
                ? "Mic Source Name (e.g., Front Desk Mic)"
                : isAnyAudioModeSelected
                ? "Stream / Source Name (optional)"
                : "Camera / Source Name"
            }
            value={cameraName}
            onChange={(e) => setCameraName(e.target.value)}
            required={srcType === "live_mic" || !isAnyAudioModeSelected}
          />

          {/* location */}
          {isAnyAudioModeSelected && (
            <input
              className="camera-input"
              placeholder="Source Location (e.g., Reception, Sales Floor)"
              value={cameraLocation}
              onChange={(e) => setCameraLocation(e.target.value)}
            />
          )}

          {/* analysis mode selection - only for Customer Service Monitor */}
          {isCustomerServiceMonitorSelected && (
            <div style={{ marginBottom: "1rem" }}>
              <label className="dropdown-label" style={{ fontSize: "1em", marginBottom: "0.5rem", display: "block" }}>
                Analysis Mode:
              </label>
              <div className="radio-row" style={{ gap: "1rem", flexWrap: "wrap" }}>
                <label>
                  <input
                    type="radio"
                    value="Customer_sentiment_analyzer"
                    checked={selectedAnalysisMode === "Customer_sentiment_analyzer"}
                    onChange={(e) => {
                      setSelectedAnalysisMode(e.target.value);
                      setError(null);
                    }}
                  />
                  Customer Sentiment Analyzer
                </label>
                <label>
                  <input
                    type="radio"
                    value="Referral_tracker"
                    checked={selectedAnalysisMode === "Referral_tracker"}
                    onChange={(e) => {
                      setSelectedAnalysisMode(e.target.value);
                      setError(null);
                    }}
                  />
                  Referral Tracker
                </label>
                <label>
                  <input
                    type="radio"
                    value="Diversion_tracker"
                    checked={selectedAnalysisMode === "Diversion_tracker"}
                    onChange={(e) => {
                      setSelectedAnalysisMode(e.target.value);
                      setError(null);
                    }}
                  />
                  Diversion Tracker
                </label>
              </div>
            </div>
          )}

          {/* companion RTSP for live-mic */}
          {srcType === "live_mic" && (
            <input
              className="camera-input"
              placeholder="(Optional) RTSP video URL for this mic"
              value={micVideoRtspUrl}
              onChange={(e) => setMicVideoRtspUrl(e.target.value)}
            />
          )}

          {/* RTSP field */}
          {srcType === "rtsp" && (
            <input
              className="camera-input"
              placeholder={
                isAnyAudioModeSelected ? "RTSP Stream URL" : "Camera RTSP URL"
              }
              value={url}
              onChange={(e) => {
                setUrl(e.target.value);
                setError(null);
              }}
              required
            />
          )}

          {/* local file / link */}
          {srcType === "local" && (
            <>
              <div
                className={`upload-box ${file ? "has-file" : ""}`}
                onDragOver={(e) => e.preventDefault()}
                onDrop={dropFile}
              >
                <input
                  type="file"
                  id="filePick"
                  accept={fileInputAcceptType}
                  style={{ display: "none" }}
                  onChange={pickFile}
                />
                <label htmlFor="filePick" className="upload-label">
                  {file ? `Selected: ${file.name}` : fileInputPlaceholder}
                </label>
              </div>
              <p style={{ textAlign: "center", margin: "8px 0" }}>
                or paste S3/HTTP(S) link:
              </p>
              <input
                className="camera-input"
                placeholder={`https://link.com/${
                  isAnyAudioModeSelected ? "audio_or_video" : "video.mp4"
                }`}
                value={url}
                onChange={(e) => {
                  setUrl(e.target.value);
                  if (e.target.value.trim()) setFile(null);
                  setError(null);
                }}
              />
            </>
          )}

          {isLoading && <p className="status-message">Submitting…</p>}
          {error && (
            <p className="error-message" style={{ marginTop: "1rem" }}>
              Error: {error}
            </p>
          )}

          <div className="button-group" style={{ marginTop: "1.5rem" }}>
            <button
              type="button"
              className="button-secondary"
              onClick={handleBackStep}
            >
              Back
            </button>
            <button
              type="submit"
              className="next-button"
              disabled={isLoading}
              title={selectedCameraIds.length > 1 ? `Start processing ${selectedCameraIds.length} cameras` : "Start single camera analysis"}
            >
              {isLoading ? "Starting…" :
               selectedCameraIds.length > 1 ? `Start Processing (${selectedCameraIds.length} cameras)` : "Start Analysis"}
            </button>
          </div>
        </div>

        {/* registered sources side-box (unchanged) */}
        {selectedAnalysisOption && !isComingSoonSelected && (
          <div className="registered-cameras-sidebox">
            {isFetchingCameras && (
              <p className="status-message">
                Loading registered sources for {selectedAnalysisOption}…
              </p>
            )}
            {!isFetchingCameras &&
              registeredCamerasForCurrentAgent.length > 0 && (
                <>
                  <h4>Registered Sources for {selectedAnalysisOption}:</h4>
                  

                  {/* Info message for audio modes */}
                  {selectedCameraIds.length > 1 && isAnyAudioModeSelected && (
                    <div style={{ marginBottom: "0.5rem", padding: "0.5rem", backgroundColor: "#e3f2fd", borderRadius: "4px", fontSize: "0.85rem", color: "#1976d2" }}>
                      ℹ️ Bulk processing for {selectedAnalysisOption} supports RTSP sources only.
                    </div>
                  )}

                  {/* Smart Selection Controls */}
                  <div className="selection-controls" style={{ marginBottom: "1rem", padding: "0.5rem", backgroundColor: "#f5f5f5", borderRadius: "4px" }}>
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "0.5rem" }}>
                      <span>Selected: {selectedCameraIds.length} of {filteredRegisteredCameras.length}</span>
                      <button
                        type="button"
                        className="button-secondary"
                        onClick={selectedCameraIds.length === filteredRegisteredCameras.length ? handleDeselectAll : handleSelectAll}
                        style={{ fontSize: "0.8rem", padding: "0.3rem 0.6rem" }}
                      >
                        {selectedCameraIds.length === 0 ?
                          `Select All (${filteredRegisteredCameras.length})` :
                          selectedCameraIds.length === filteredRegisteredCameras.length ?
                          "Deselect All" :
                          `Select All (${filteredRegisteredCameras.length - selectedCameraIds.length} remaining)`
                        }
                      </button>
                    </div>
                  </div>

                  <div className="registered-cameras-list">
                    {filteredRegisteredCameras.map((cam) => (
                      <div
                        key={getCameraUniqueId(cam)}
                        className="camera-card"
                        onClick={() => handleCameraCardClick(cam)}
                        style={{
                          cursor: "pointer",
                          position: "relative"
                        }}
                      >
                        {/* Checkbox for selection */}
                        <div style={{ position: "absolute", top: "0.5rem", right: "0.5rem" }}>
                          <input
                            type="checkbox"
                            checked={selectedCameraIds.includes(getCameraUniqueId(cam))}
                            onChange={(e) => {
                              e.stopPropagation();
                              handleCameraCheckboxChange(cam);
                            }}
                            style={{ transform: "scale(1.2)" }}
                          />
                        </div>
                        
                        <div style={{ display: "flex", alignItems: "center", flexWrap: "wrap", gap: "0.5rem", marginBottom: "0.5rem" }}>
                          <p className="card-name" style={{ margin: 0, flex: "1 1 auto" }}>
                            <span className="card-label">Name:</span>
                            <span style={{
                              fontWeight: cam.agent === "Customer Service Monitor" && cam.analysis_mode ? "600" : "normal",
                              color: cam.agent === "Customer Service Monitor" && cam.analysis_mode ? "#1976d2" : "inherit"
                            }}>
                              {getCameraDisplayName(cam)}
                            </span>
                          </p>

                          {/* Analysis Mode Badge */}
                          {cam.agent === "Customer Service Monitor" && cam.analysis_mode && (
                            <span style={{
                              padding: "0.25rem 0.5rem",
                              borderRadius: "12px",
                              fontSize: "0.75rem",
                              fontWeight: "500",
                              backgroundColor: cam.analysis_mode === "Customer_sentiment_analyzer" ? "#e3f2fd" :
                                              cam.analysis_mode === "Referral_tracker" ? "#f3e5f5" :
                                              cam.analysis_mode === "Diversion_tracker" ? "#e8f5e8" : "#f0f0f0",
                              color: cam.analysis_mode === "Customer_sentiment_analyzer" ? "#1976d2" :
                                     cam.analysis_mode === "Referral_tracker" ? "#7b1fa2" :
                                     cam.analysis_mode === "Diversion_tracker" ? "#2e7d32" : "#666",
                              border: `1px solid ${cam.analysis_mode === "Customer_sentiment_analyzer" ? "#bbdefb" :
                                                   cam.analysis_mode === "Referral_tracker" ? "#e1bee7" :
                                                   cam.analysis_mode === "Diversion_tracker" ? "#c8e6c9" : "#ddd"}`,
                              whiteSpace: "nowrap"
                            }}>
                              {cam.analysis_mode === "Customer_sentiment_analyzer" && "Sentiment"}
                              {cam.analysis_mode === "Referral_tracker" && "Referral"}
                              {cam.analysis_mode === "Diversion_tracker" && "Diversion"}
                            </span>
                          )}
                        </div>
                        <p className="card-url">
                          <span className="card-label">
                            {cam.url.startsWith("live_mic_source_")
                              ? "Mic ID:"
                              : "URL:"}
                          </span>
                          {cam.url.startsWith("live_mic_source_")
                            ? cam.url.replace(/^live_mic_source_/, "")
                            : cam.url.length > 50
                            ? `${cam.url.substring(0, 50)}…`
                            : cam.url}
                        </p>
                        {cam.location && (
                          <p className="card-location">
                            <span className="card-label">Location:</span>
                            <span>{cam.location}</span>
                          </p>
                        )}
                        
                        <div className="camera-card-actions">
                          <button
                            type="button"
                            className="edit-btn"
                            onClick={(e) => handleEditClick(e, cam)}
                            title="Edit camera"
                          >
                            <FiEdit2 />
                          </button>
                          <button
                            type="button"
                            className="delete-btn"
                            onClick={(e) => handleDeleteClick(e, cam)}
                            title="Delete camera"
                          >
                            <FiTrash2 />
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                </>
              )}
            {!isFetchingCameras &&
              step === 2 &&
              filteredRegisteredCameras.length === 0 && (
                <p
                  style={{
                    fontSize: "0.9em",
                    color: "var(--text-secondary)",
                    marginTop: "1rem",
                    textAlign: "center",
                  }}
                >
                  No sources registered for {selectedAnalysisOption}.
                </p>
              )}
          </div>
        )}
      </div>
    </motion.div>
  );

  /* ───── render ───── */
  return (
    <>
      <div className="camera-form-steps-container" style={{ minHeight: "520px" }}>
        <form className="camera-form" onSubmit={handleSubmit} noValidate>
        <AnimatePresence initial={false} custom={direction} mode="wait">
          {step === 1 ? (
            <motion.div
              key="step1"
              custom={direction}
              variants={variants}
              initial="enter"
              animate="center"
              exit="exit"
              transition={transition}
              className="form-step"
            >
              <div className="step1-main-layout">
                <div className="step1-radio-group-container">
                  {analysisOptions.map((opt) => (
                    <label key={opt} className="radio-option">
                      <input
                        type="radio"
                        name="analysisOption"
                        value={opt}
                        checked={selectedAnalysisOption === opt}
                        onChange={(e) => {
                          const newOpt = e.target.value;
                          setSelectedAnalysisOption(newOpt);
                          
                          if (newOpt && !newOpt.toLowerCase().includes("(coming soon)")) {
                            setShowAgentAnimation(true);
                            setTimeout(() => setShowAgentAnimation(false), 1600);
                          } else {
                            setShowAgentAnimation(false);
                          }
                        }}
                        style={{ marginRight: "0.5rem" }}
                      />
                      {opt}
                    </label>
                  ))}
                </div>

                {selectedAnalysisOption && (
                  <div className="step1-agent-details-container">
                    <div className="agent-details-label-wrapper">
                      <label className="dropdown-label">Agent Details :</label>
                      {showAgentAnimation && !isComingSoonSelected && (
                        <span className="bouncing-star">✨</span>
                      )}
                    </div>
                    <div
                      className={`agent-details-textarea-wrapper ${
                        showAgentAnimation && !isComingSoonSelected
                          ? "animate-gradient-border"
                          : ""
                      }`}
                    >
                      <textarea
                        className={`camera-textarea showcase-prompt ${
                          isComingSoonSelected ? "prompt-coming-soon" : ""
                        }`}
                        value={displayedShowcasePrompt}
                        readOnly
                        rows={6}
                      />
                    </div>
                  </div>
                )}
              </div>

              {error && (
                <p
                  className="error-message"
                  style={{ marginTop: "1rem", marginBottom: "1rem" }}
                >
                  Error: {error}
                </p>
              )}

              <button
                type="button"
                className="next-button"
                onClick={handleNextStep}
                disabled={
                  !selectedAnalysisOption || isLoading || isComingSoonSelected
                }
              >
                Next
              </button>
            </motion.div>
          ) : (
            step2Content
          )}
        </AnimatePresence>
      </form>
      </div>

      {/* Edit Camera Modal */}
      <ModalPortal isOpen={!!editingCamera} onClose={handleEditCancel}>
        <div className="modal-overlay" onClick={handleEditCancel}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Edit Camera</h3>
              <button type="button" className="modal-close" onClick={handleEditCancel}>
                <FiX />
              </button>
            </div>
            <form onSubmit={handleEditSubmit}>
              <div className="form-group">
                <label>Camera Name:</label>
                <input
                  type="text"
                  value={editFormData.name}
                  onChange={(e) => setEditFormData({...editFormData, name: e.target.value})}
                  required
                />
              </div>
              <div className="form-group">
                <label>Location:</label>
                <input
                  type="text"
                  value={editFormData.location}
                  onChange={(e) => setEditFormData({...editFormData, location: e.target.value})}
                />
              </div>
              <div className="form-group">
                <label>URL:</label>
                <input
                  type="text"
                  value={editFormData.url}
                  onChange={(e) => setEditFormData({...editFormData, url: e.target.value})}
                  required
                />
              </div>
              
              {/* Analysis Mode for Customer Service Monitor only */}
              {editFormData.agent === "Customer Service Monitor" && (
                <div className="form-group">
                  <label>Analysis Mode:</label>
                  <div className="radio-row" style={{ gap: "1rem", marginTop: "0.5rem" }}>
                    <label>
                      <input
                        type="radio"
                        value="Customer_sentiment_analyzer"
                        checked={editFormData.analysis_mode === "Customer_sentiment_analyzer"}
                        onChange={(e) => setEditFormData({...editFormData, analysis_mode: e.target.value})}
                      />
                      Customer Sentiment Analyzer
                    </label>
                    <label>
                      <input
                        type="radio"
                        value="Referral_tracker"
                        checked={editFormData.analysis_mode === "Referral_tracker"}
                        onChange={(e) => setEditFormData({...editFormData, analysis_mode: e.target.value})}
                      />
                      Referral Tracker
                    </label>
                    <label>
                      <input
                        type="radio"
                        value="Diversion_tracker"
                        checked={editFormData.analysis_mode === "Diversion_tracker"}
                        onChange={(e) => setEditFormData({...editFormData, analysis_mode: e.target.value})}
                      />
                      Diversion Tracker
                    </label>
                  </div>
                </div>
              )}
              
              <div className="modal-actions">
                <button type="button" className="button-secondary" onClick={handleEditCancel}>
                  Cancel
                </button>
                <button type="submit" className="button-primary">
                  Save Changes
                </button>
              </div>
            </form>
          </div>
        </div>
      </ModalPortal>

      {/* Delete Confirmation Modal */}
      <ModalPortal isOpen={!!showDeleteConfirm} onClose={handleDeleteCancel}>
        <div className="modal-overlay" onClick={handleDeleteCancel}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Delete Camera</h3>
              <button type="button" className="modal-close" onClick={handleDeleteCancel}>
                <FiX />
              </button>
            </div>
            <div className="modal-body">
              <p>Are you sure you want to delete the camera <strong>"{showDeleteConfirm?.name}"</strong>?</p>
              <p className="warning-text">This action cannot be undone.</p>
            </div>
            <div className="modal-actions">
              <button type="button" className="button-secondary" onClick={handleDeleteCancel}>
                Cancel
              </button>
              <button type="button" className="button-danger" onClick={handleDeleteConfirm}>
                Delete
              </button>
            </div>
          </div>
        </div>
      </ModalPortal>
    </>
  );
}

CameraForm.propTypes = {
  onSubmit: PropTypes.func.isRequired,
  isLoading: PropTypes.bool,
};